import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import toast from 'components/partials/toast/toast';

import useAwardCalculationDraft from './use-award-calculation-draft';
import { usePopulationsDraft } from './use-populations-draft';

import { useUpdateRegime } from '../../../hooks/use-aid-regimes';

import {
  AWARD_TYPE_OPTIONS,
  CALCULATED_BY_OPTIONS,
  buildRangeKey,
  getUniqueRangesFromCalculation,
  sortRangesForDisplay,
} from '../definition-tab.constants';

import type { AwardType, CalculatedBy, Range } from '../definition-tab.constants';

type RangesByType = Record<string, Range[]>;

const EMPTY_RANGES_BY_TYPE: RangesByType = AWARD_TYPE_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.value]: [] }),
  {} as RangesByType
);

const serializeRanges = (r: Range[]): string => r.map(buildRangeKey).join('|');

const serializeRangesByType = (rangesByType: RangesByType): string =>
  AWARD_TYPE_OPTIONS.map(
    (option) =>
      `${option.value}:${serializeRanges(rangesByType[option.value] ?? [])}`
  ).join(';');

interface UseRegimeDraftParams {
  selectedRegime?: Record<string, unknown> | null;
  academicRanks?: unknown[];
  customLogicCategories?: unknown[];
  userId?: string | null;
  modelId?: string | number;
}

const useRegimeDraft = ({
  selectedRegime,
  academicRanks,
  customLogicCategories,
  userId,
  modelId,
}: UseRegimeDraftParams) => {
  const [awardType, setAwardType] = useState(AWARD_TYPE_OPTIONS[0].value);
  const [calculatedBy, setCalculatedBy] = useState(
    CALCULATED_BY_OPTIONS[0].value
  );
  const [rangesByType, setRangesByType] = useState(EMPTY_RANGES_BY_TYPE);
  const definitionInitRef = useRef({
    awardType: AWARD_TYPE_OPTIONS[0].value,
    calculatedBy: CALCULATED_BY_OPTIONS[0].value,
    rangesByType: EMPTY_RANGES_BY_TYPE,
  });

  useEffect(() => {
    const nextAwardType = (selectedRegime?.awardType ??
      AWARD_TYPE_OPTIONS[0].value) as AwardType;
    const nextCalculatedBy = (selectedRegime?.calculatedBy ??
      CALCULATED_BY_OPTIONS[0].value) as CalculatedBy;
    const extracted = getUniqueRangesFromCalculation(
      selectedRegime?.calculation as never
    );
    const nextRanges = sortRangesForDisplay(extracted);
    const nextRangesByType: RangesByType = {
      ...EMPTY_RANGES_BY_TYPE,
      [nextAwardType]: nextRanges,
    };

    setAwardType(nextAwardType);
    setCalculatedBy(nextCalculatedBy);
    setRangesByType(nextRangesByType);
    definitionInitRef.current = {
      awardType: nextAwardType,
      calculatedBy: nextCalculatedBy,
      rangesByType: nextRangesByType,
    };
  }, [selectedRegime?.id]);

  const ranges = useMemo(
    () => rangesByType[awardType] ?? [],
    [rangesByType, awardType]
  );

  const handleAwardTypeChange = useCallback((value: string | undefined) => {
    if (value !== undefined) setAwardType(value as AwardType);
  }, []);

  const handleCalculatedByChange = useCallback((value: string | undefined) => {
    if (value !== undefined) setCalculatedBy(value as CalculatedBy);
  }, []);

  const handleRangesChange = useCallback(
    (updatedRanges: Range[]) => {
      setRangesByType((prev: RangesByType) => ({ ...prev, [awardType]: updatedRanges }));
    },
    [awardType]
  );

  const {
    rankIds,
    rankIdToDisplayNumber,
    getCell,
    updateCell,
    hasChanges: matrixHasChanges,
    resetDraft: resetMatrixDraft,
    toCalculationPayload,
    markAsSaved: markMatrixSaved,
  } = useAwardCalculationDraft({
    regime: selectedRegime as never,
    ranges: ranges as unknown as Record<string, unknown>[],
    academicRanks: academicRanks as unknown as Array<{ id: string | number; rank: number }>,
  });

  const {
    baselineSelected,
    toggleBaseline,
    activeCategoryIds,
    activeCategories,
    optionByCategory,
    toggleCategory,
    removeCategory,
    setOption,
    customLogicIds: draftCustomLogicIds,
    hasChanges: populationsHasChanges,
    resetDraft: resetPopulationsDraft,
    markAsSaved: markPopulationsSaved,
  } = usePopulationsDraft({
    regime: selectedRegime as never,
    customLogicCategories: customLogicCategories as never,
  });

  const definitionHasChanges =
    awardType !== definitionInitRef.current.awardType ||
    calculatedBy !== definitionInitRef.current.calculatedBy ||
    serializeRangesByType(rangesByType) !==
      serializeRangesByType(definitionInitRef.current.rangesByType);

  const hasChanges =
    matrixHasChanges || definitionHasChanges || populationsHasChanges;

  const updateRegimeMutation = useUpdateRegime();
  const isSaving = updateRegimeMutation.isPending;

  const handleSave = useCallback(async () => {
    if (!selectedRegime || !userId || !modelId) return;

    const data = {
      name: selectedRegime.name,
      population: baselineSelected,
      customLogicIds: draftCustomLogicIds,
      awardType,
      calculatedBy,
      calculation: toCalculationPayload(),
      groupId: selectedRegime.groupId,
      sortOrder: selectedRegime.sortOrder,
    };

    try {
      await updateRegimeMutation.mutateAsync({
        userId,
        modelId,
        regimeId: selectedRegime.id as unknown as string | number,
        data,
      });

      markMatrixSaved();
      markPopulationsSaved();
      definitionInitRef.current = {
        awardType,
        calculatedBy,
        rangesByType,
      };
      toast.success({ title: 'Regime saved successfully' });
    } catch {
      toast.error({ title: 'Failed to save regime' });
    }
  }, [
    selectedRegime,
    userId,
    modelId,
    baselineSelected,
    draftCustomLogicIds,
    awardType,
    calculatedBy,
    rangesByType,
    toCalculationPayload,
    updateRegimeMutation,
    markMatrixSaved,
    markPopulationsSaved,
  ]);

  const handleDiscard = useCallback(() => {
    setAwardType(definitionInitRef.current.awardType);
    setCalculatedBy(definitionInitRef.current.calculatedBy);
    setRangesByType(definitionInitRef.current.rangesByType);
    resetMatrixDraft();
    resetPopulationsDraft();
  }, [resetMatrixDraft, resetPopulationsDraft]);

  return {
    awardType,
    calculatedBy,
    ranges,
    onAwardTypeChange: handleAwardTypeChange,
    onCalculatedByChange: handleCalculatedByChange,
    onRangesChange: handleRangesChange,

    rankIds,
    rankIdToDisplayNumber,
    getCell,
    updateCell,

    baselineSelected,
    toggleBaseline,
    activeCategoryIds,
    activeCategories,
    optionByCategory,
    toggleCategory,
    removeCategory,
    setOption,

    hasChanges,
    isSaving,
    handleSave,
    handleDiscard,
  };
};

export default useRegimeDraft;
